import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Drawer, Button, Row, Col, Typography, Space, message, Input, Cascader, Image } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useQuery, useMutation } from '@apollo/client';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import * as mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { GET_ADDRESS_LOCATION_REVIEW_BY_ID, GetAddressLocationReviewByIdResponse } from '../graphql/queries/getAddressLocationReviewById';
import { REVIEW_ADDRESS_LOCATION_REVIEW, ReviewAddressLocationReviewResponse, AddressLocationReviewReviewInput } from '../graphql/mutations/reviewAddressLocationReview';
import {
  GET_ADDRESS_LOCATION_TYPE_DICT_CONFIG,
  GetAddressLocationTypeDictConfigResponse,
  AddressLocationTypeDictItem,
} from '../graphql/queries/getAddressLocationTypeDictConfig';
import { HasResource } from '../../../../components/HasResource';
import { ResourceCode } from 'constants/resourceCodes';

const { Text } = Typography;

// Google Maps API Key
const GOOGLE_MAPS_API_KEY = 'AIzaSyD8huYA1dL8uG9O1a77bnW3JtpTjUimjmc';

// Mapbox Access Token
const MAPBOX_ACCESS_TOKEN = 'pk.eyJ1IjoibGl4aWFvaHVpdGl0YSIsImEiOiJjbWNsc2J3aTcwZDkyMmtwdGxiZW95YWt6In0.qoTeK6P5iQ8Mq62KdImwBQ';
(mapboxgl as any).accessToken = MAPBOX_ACCESS_TOKEN;

type MapProvider = 'google' | 'mapbox';
// 底图图层：街道 / 卫星，两个提供商共用同一状态，切换提供商时图层保持
type MapLayer = 'streets' | 'satellite';
const MAPBOX_STYLE_BY_LAYER: Record<MapLayer, string> = {
  streets: 'mapbox://styles/mapbox/streets-v12',
  satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
};
// Google 卫星模式用 hybrid（卫星 + 路名门牌标注），审核时便于对照地址
const GOOGLE_MAP_TYPE_BY_LAYER: Record<MapLayer, string> = {
  streets: 'roadmap',
  satellite: 'hybrid',
};

// 坐标颜色定义
const COORDINATE_COLORS = {
  original: '#4285F4', // 蓝色 - 原始坐标
  reported: '#FBBC04', // 橙色 - 待审坐标
  delivered: '#34A853', // 绿色 - 妥投坐标
  corrected: '#EA4335', // 红色 - 修正后坐标
  approved: '#9C27B0', // 紫色 - 审核通过坐标
  driver: '#FF6D00', // 深橙色 - 司机定位坐标
};

// 上报来源展示文案（只读，不可修改）
const REPORT_SOURCE_LABELS: Record<string, string> = {
  DRIVER: '司机上报',
  BACKEND: '后台修改',
};

interface AddressLocationReviewDrawerProps {
  visible: boolean;
  onClose: () => void;
  reviewId: string | null;
  onRefresh?: () => void;
  // 审核成功后回调：由父组件决定跳到下一条待审核还是关闭抽屉并刷新
  onReviewed?: () => void;
}

const AddressLocationReviewDrawer: React.FC<AddressLocationReviewDrawerProps> = ({
  visible,
  onClose,
  reviewId,
  onRefresh,
  onReviewed,
}) => {
  // 处理关闭抽屉，关闭时刷新列表
  const handleClose = () => {
    onRefresh?.();
    onClose();
  };
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [mapReady, setMapReady] = useState(false); // 地图瓦片是否完全加载
  const [center, setCenter] = useState<{ lat: number; lng: number } | null>(null);
  // 修正后坐标（用户通过移动地图获取）
  const [correctedLatitude, setCorrectedLatitude] = useState<string>('');
  const [correctedLongitude, setCorrectedLongitude] = useState<string>('');
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number } | null>(null);
  // 防止循环更新的标志
  const isUpdatingFromInputRef = useRef(false);

  // 地图切换相关
  const [mapProvider, setMapProvider] = useState<MapProvider>('google');
  const [mapLayer, setMapLayer] = useState<MapLayer>('streets');
  // 记录 Mapbox 当前已应用的 style，避免对同一 style 重复 setStyle 触发瓦片重拉
  const mapboxAppliedStyleRef = useRef<string | null>(null);
  const [mapboxNavExpanded, setMapboxNavExpanded] = useState(false);
  const [mapboxZoom, setMapboxZoom] = useState<number>(15);
  const mapboxContainerRef = useRef<HTMLDivElement>(null);
  const mapboxInstanceRef = useRef<mapboxgl.Map | null>(null);
  const mapboxMarkersRef = useRef<mapboxgl.Marker[]>([]);

  // 使用 useJsApiLoader 加载 Google Maps
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    preventGoogleFontsLoading: true,
  });

  // 查询审核数据
  const { data, loading, refetch } = useQuery<GetAddressLocationReviewByIdResponse>(
    GET_ADDRESS_LOCATION_REVIEW_BY_ID,
    {
      variables: { id: reviewId ? Number(reviewId) : null },
      skip: !reviewId || !visible,
      fetchPolicy: 'cache-and-network',
    }
  );

  const { data: typeDictData } = useQuery<GetAddressLocationTypeDictConfigResponse>(
    GET_ADDRESS_LOCATION_TYPE_DICT_CONFIG,
    { fetchPolicy: 'cache-first' }
  );
  const typeDictItems: AddressLocationTypeDictItem[] = useMemo(
    () => (typeDictData?.getAddressLocationTypeDictConfig ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder),
    [typeDictData]
  );

  const [approvedTypeValue, setApprovedTypeValue] = useState<string[] | null>(null);

  const typeCascaderOptions = useMemo(() => {
    const grouped = new Map<string, { value: string; label: string; children: { value: string; label: string }[] }>();
    for (const item of typeDictItems) {
      const primary = grouped.get(item.primaryCode);
      const primaryLabel = item.primaryNameZh;
      const secondary = { value: item.secondaryCode, label: item.secondaryNameZh };
      if (!primary) {
        grouped.set(item.primaryCode, { value: item.primaryCode, label: primaryLabel, children: [secondary] });
      } else {
        primary.children.push(secondary);
      }
    }
    return Array.from(grouped.values());
  }, [typeDictItems]);

  const findItemBySecondary = useCallback(
    (secondaryCode: string | null | undefined) =>
      typeDictItems.find((it) => it.secondaryCode === secondaryCode) ?? null,
    [typeDictItems]
  );
  const formatTypeLabel = useCallback(
    (primaryCode: string | null | undefined, secondaryCode: string | null | undefined) => {
      if (!primaryCode || !secondaryCode) return '-';
      const match = findItemBySecondary(secondaryCode);
      if (!match) return `${primaryCode} / ${secondaryCode}`;
      return `${match.primaryNameZh} / ${match.secondaryNameZh}`;
    },
    [findItemBySecondary]
  );

  // 审核地址定位记录 Mutation
  const [reviewAddressLocationReview, { loading: reviewLoading }] = useMutation<ReviewAddressLocationReviewResponse>(
    REVIEW_ADDRESS_LOCATION_REVIEW,
    {
      onCompleted: (data) => {
        if (data.reviewAddressLocationReview.success) {
          message.success(data.reviewAddressLocationReview.message || '操作成功');
          // 审核成功后不立即刷新，交由父组件跳到下一条待审核；无下一条时父组件负责关闭抽屉并刷新
          onReviewed?.();
        } else {
          message.error(data.reviewAddressLocationReview.message || '操作失败');
        }
      },
      onError: (error) => {
        message.error(error.message || '操作失败，请稍后重试');
      },
    }
  );

  // 分别跟踪两个按钮的 loading 状态
  const [approveLoading, setApproveLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);

  const review = data?.getAddressLocationReviewById;
  // 后台修改来源：原始地址走组件拼接格式，且不展示妥投/司机坐标、司机上报类型、审核地址类型
  const isBackendSource = review?.reportSource === 'BACKEND';

  // 地图加载回调
  const onMapLoad = useCallback((mapInstance: google.maps.Map) => {
    setMap(mapInstance);
    
    // 等待地图完全加载后才标记为准备好
    google.maps.event.addListenerOnce(mapInstance, 'tilesloaded', () => {
      setMapReady(true);
    });
  }, []);

  // 地图卸载回调
  const onMapUnmount = useCallback(() => {
    setMap(null);
    setMapCenter(null);
    setMapReady(false);
  }, []);

  // 使用 ref 存储上一次设置的 center，避免重复设置
  const lastCenterRef = useRef<{ lat: number; lng: number } | null>(null);

  // 根据数据设置地图中心点和修正后坐标（只在 review.id 变化时设置一次）
  useEffect(() => {
    if (!review || !isLoaded) return;

    // 计算要显示的坐标（用于地图中心）
    let lat: number;
    let lng: number;
    
    if (review.status === 'APPROVED' && review.approvedLatitude && review.approvedLongitude) {
      lat = parseFloat(review.approvedLatitude);
      lng = parseFloat(review.approvedLongitude);
    } else {
      // 待审核或拒绝状态，默认显示待审坐标
      lat = parseFloat(review.reportedLatitude || review.latitude);
      lng = parseFloat(review.reportedLongitude || review.longitude);
    }

    // 只有当坐标真正改变时才更新
    const newCenter = { lat, lng };
    if (!lastCenterRef.current || 
        lastCenterRef.current.lat !== newCenter.lat || 
        lastCenterRef.current.lng !== newCenter.lng) {
      lastCenterRef.current = newCenter;
      setCenter(newCenter);
    }

    // 设置修正后坐标：如果有审核通过坐标则使用，否则使用待审坐标
    const correctedLat = review.approvedLatitude 
      ? parseFloat(review.approvedLatitude) 
      : parseFloat(review.reportedLatitude || review.latitude);
    const correctedLng = review.approvedLongitude 
      ? parseFloat(review.approvedLongitude) 
      : parseFloat(review.reportedLongitude || review.longitude);
    
    const latStr = correctedLat.toFixed(7);
    const lngStr = correctedLng.toFixed(7);
    correctedLatitudeRef.current = latStr;
    correctedLongitudeRef.current = lngStr;
    setCorrectedLatitude(latStr);
    setCorrectedLongitude(lngStr);
    setMapCenter({ lat: correctedLat, lng: correctedLng });
  }, [review?.id, isLoaded, review?.approvedLatitude, review?.approvedLongitude, review?.reportedLatitude, review?.reportedLongitude, review?.latitude, review?.longitude]); // 只在 review.id 变化时重新设置

  // 地图加载完成后，设置地图中心和缩放级别
  const mapInitializedRef = useRef(false);
  const lastSetCenterRef = useRef<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    if (map && center) {
      // 检查 center 是否真正改变了
      const centerChanged = !lastSetCenterRef.current ||
        lastSetCenterRef.current.lat !== center.lat ||
        lastSetCenterRef.current.lng !== center.lng;
      
      if (centerChanged || !mapInitializedRef.current) {
        map.setCenter(center);
        map.setZoom(15); // 设置合适的缩放级别
        lastSetCenterRef.current = center;
        mapInitializedRef.current = true;
        
        // 强制触发地图重新渲染，确保 marker 显示
        setTimeout(() => {
          if (map) {
            google.maps.event.trigger(map, 'resize');
          }
        }, 100);
      }
    }
  }, [map, center]);

  // 使用 ref 存储最新的坐标值，避免依赖项变化
  const correctedLatitudeRef = useRef<string>('');
  const correctedLongitudeRef = useRef<string>('');

  // 生成 SVG 图标的统一函数
  const generatePinIconSvg = useCallback((color: string, size: { width: number; height: number } = { width: 42, height: 52 }): string => {
    return `
      <svg xmlns="http://www.w3.org/2000/svg" width="${size.width}" height="${size.height}" viewBox="0 0 42 52">
        <path d="M21 2C12.7 2 6 8.7 6 17c0 11.3 15 23 15 23s15-11.7 15-23c0-8.3-6.7-15-15-15z" fill="${color}"/>
        <circle cx="21" cy="17" r="6" fill="#FFFFFF"/>
      </svg>
    `;
  }, []);

  // 生成 data URI 格式的图标（用于 img 标签）
  const generatePinIconDataUri = useCallback((color: string, size: { width: number; height: number } = { width: 20, height: 24 }): string => {
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(generatePinIconSvg(color, size))}`;
  }, [generatePinIconSvg]);

  // 创建 Marker icon 的辅助函数（用于 Google Maps）
  const createMarkerIcon = useCallback((color: string): google.maps.Icon => {
    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(generatePinIconSvg(color, { width: 42, height: 52 })),
      scaledSize: {width: 42, height: 52} as google.maps.Size,
      anchor: {x: 21, y: 52} as google.maps.Point,
    };
  }, [generatePinIconSvg]);

  // 缓存静态 marker - 稳定不变
  const staticMarkers = useMemo(() => {
    if (!review || !isLoaded || !mapReady) {
      return null;
    }

    const markers = [];

    // 原始坐标（蓝色）
    markers.push(
      <Marker
        key={`original-${review.id}`}
        position={{
          lat: parseFloat(review.latitude),
          lng: parseFloat(review.longitude),
        }}
        icon={createMarkerIcon(COORDINATE_COLORS.original)}
        title={`原始坐标: ${review.latitude}, ${review.longitude}`}
        zIndex={1}
      />
    );

    // 待审坐标（橙色）
    markers.push(
      <Marker
        key={`reported-${review.id}`}
        position={{
          lat: parseFloat(review.reportedLatitude),
          lng: parseFloat(review.reportedLongitude),
        }}
        icon={createMarkerIcon(COORDINATE_COLORS.reported)}
        title={`待审坐标: ${review.reportedLatitude}, ${review.reportedLongitude}`}
        zIndex={1}
      />
    );

    // 司机坐标（深橙色）——后台修改来源不展示
    if (!isBackendSource && review.driverLatitude && review.driverLongitude) {
      markers.push(
        <Marker
          key={`driver-${review.id}`}
          position={{
            lat: parseFloat(review.driverLatitude),
            lng: parseFloat(review.driverLongitude),
          }}
          icon={createMarkerIcon(COORDINATE_COLORS.driver)}
          title={`司机坐标: ${review.driverLatitude}, ${review.driverLongitude}`}
          zIndex={1}
        />
      );
    }

    // 妥投坐标（绿色）——后台修改来源不展示
    if (!isBackendSource && review.deliveredLatitude && review.deliveredLongitude) {
      markers.push(
        <Marker
          key={`delivered-${review.id}`}
          position={{
            lat: parseFloat(review.deliveredLatitude),
            lng: parseFloat(review.deliveredLongitude),
          }}
          icon={createMarkerIcon(COORDINATE_COLORS.delivered)}
          title={`妥投坐标: ${review.deliveredLatitude}, ${review.deliveredLongitude}`}
          zIndex={1}
        />
      );
    }

    // 审核通过坐标（紫色）
    if (review.approvedLatitude && review.approvedLongitude) {
      markers.push(
        <Marker
          key={`approved-${review.id}`}
          position={{
            lat: parseFloat(review.approvedLatitude),
            lng: parseFloat(review.approvedLongitude),
          }}
          icon={createMarkerIcon(COORDINATE_COLORS.approved)}
          title={`审核通过坐标: ${review.approvedLatitude}, ${review.approvedLongitude}`}
          zIndex={1}
        />
      );
    }

    return markers;
  }, [review?.id, isLoaded, mapReady, createMarkerIcon, isBackendSource]);


  // 统一的地图跳转函数
  const jumpToCoordinate = useCallback((lat: number, lng: number) => {
    // 跳转 Google Map
    if (map) {
      map.setCenter({ lat, lng });
      map.setZoom(15);
      setTimeout(() => {
        if (map) {
          google.maps.event.trigger(map, 'resize');
        }
      }, 100);
    }

    // 跳转 Mapbox
    if (mapboxInstanceRef.current) {
      mapboxInstanceRef.current.jumpTo({ center: [lng, lat], zoom: 15 });
    }

    // 同步更新修正后坐标（仅在待审核状态下）
    if (review?.status === 'PENDING') {
      const latStr = lat.toFixed(7);
      const lngStr = lng.toFixed(7);
      correctedLatitudeRef.current = latStr;
      correctedLongitudeRef.current = lngStr;
      setCorrectedLatitude(latStr);
      setCorrectedLongitude(lngStr);
      setMapCenter({ lat, lng });
    }
  }, [map, review?.status]);

  // 创建 Mapbox marker 的 DOM 元素
  const createMapboxMarkerElement = useCallback((color: string): HTMLDivElement => {
    const el = document.createElement('div');
    el.style.width = '42px';
    el.style.height = '52px';
    el.innerHTML = generatePinIconSvg(color, { width: 42, height: 52 });
    return el;
  }, [generatePinIconSvg]);

  // 清除 Mapbox markers
  const clearMapboxMarkers = useCallback(() => {
    mapboxMarkersRef.current.forEach(m => m.remove());
    mapboxMarkersRef.current = [];
  }, []);

  // 在 Mapbox 上添加与 Google Map 相同的 markers
  const addMapboxMarkers = useCallback(() => {
    if (!mapboxInstanceRef.current || !review) return;
    clearMapboxMarkers();

    const mbMap = mapboxInstanceRef.current;

    // 原始坐标（蓝色）
    mapboxMarkersRef.current.push(
      new mapboxgl.Marker({ element: createMapboxMarkerElement(COORDINATE_COLORS.original), anchor: 'bottom' })
        .setLngLat([parseFloat(review.longitude), parseFloat(review.latitude)])
        .addTo(mbMap)
    );

    // 待审坐标（橙色）
    mapboxMarkersRef.current.push(
      new mapboxgl.Marker({ element: createMapboxMarkerElement(COORDINATE_COLORS.reported), anchor: 'bottom' })
        .setLngLat([parseFloat(review.reportedLongitude), parseFloat(review.reportedLatitude)])
        .addTo(mbMap)
    );

    // 司机坐标（深橙色）——后台修改来源不展示
    if (!isBackendSource && review.driverLatitude && review.driverLongitude) {
      mapboxMarkersRef.current.push(
        new mapboxgl.Marker({ element: createMapboxMarkerElement(COORDINATE_COLORS.driver), anchor: 'bottom' })
          .setLngLat([parseFloat(review.driverLongitude), parseFloat(review.driverLatitude)])
          .addTo(mbMap)
      );
    }

    // 妥投坐标（绿色）——后台修改来源不展示
    if (!isBackendSource && review.deliveredLatitude && review.deliveredLongitude) {
      mapboxMarkersRef.current.push(
        new mapboxgl.Marker({ element: createMapboxMarkerElement(COORDINATE_COLORS.delivered), anchor: 'bottom' })
          .setLngLat([parseFloat(review.deliveredLongitude), parseFloat(review.deliveredLatitude)])
          .addTo(mbMap)
      );
    }

    // 审核通过坐标（紫色）
    if (review.approvedLatitude && review.approvedLongitude) {
      mapboxMarkersRef.current.push(
        new mapboxgl.Marker({ element: createMapboxMarkerElement(COORDINATE_COLORS.approved), anchor: 'bottom' })
          .setLngLat([parseFloat(review.approvedLongitude), parseFloat(review.approvedLatitude)])
          .addTo(mbMap)
      );
    }
  }, [review, clearMapboxMarkers, createMapboxMarkerElement, isBackendSource]);

  // 初始化 Mapbox 地图
  const initMapbox = useCallback(() => {
    if (!mapboxContainerRef.current || mapboxInstanceRef.current) return;

    const initCenter = center || { lat: 0, lng: 0 };
    const initialStyle = MAPBOX_STYLE_BY_LAYER[mapLayer];
    const mbMap = new mapboxgl.Map({
      container: mapboxContainerRef.current,
      style: initialStyle,
      center: [initCenter.lng, initCenter.lat],
      zoom: 15,
    });
    mapboxAppliedStyleRef.current = initialStyle;

    mbMap.on('move', () => {
      if (review?.status === 'PENDING' && !isUpdatingFromInputRef.current) {
        const c = mbMap.getCenter();
        const latStr = c.lat.toFixed(7);
        const lngStr = c.lng.toFixed(7);
        correctedLatitudeRef.current = latStr;
        correctedLongitudeRef.current = lngStr;
        setCorrectedLatitude(latStr);
        setCorrectedLongitude(lngStr);
      }
    });

    mbMap.on('zoom', () => {
      setMapboxZoom(mbMap.getZoom());
    });

    mbMap.on('load', () => {
      mapboxInstanceRef.current = mbMap;
      addMapboxMarkers();
    });
  }, [center, review?.status, addMapboxMarkers, mapLayer]);

  // 销毁 Mapbox 地图
  const destroyMapbox = useCallback(() => {
    clearMapboxMarkers();
    if (mapboxInstanceRef.current) {
      mapboxInstanceRef.current.remove();
      mapboxInstanceRef.current = null;
    }
    mapboxAppliedStyleRef.current = null;
  }, [clearMapboxMarkers]);

  // 切换地图时同步视图
  const handleSwitchProvider = useCallback((provider: MapProvider) => {
    if (provider === mapProvider) return;

    if (provider === 'mapbox') {
      // 切到 Mapbox：先切换显示，等 DOM 渲染后初始化
      setMapProvider(provider);
      // 从 Google 读取当前视图
      const googleCenter = map?.getCenter();
      const googleZoom = map?.getZoom();

      setTimeout(() => {
        if (!mapboxInstanceRef.current) {
          initMapbox();
        }
        // 等 mapbox 初始化后同步视图
        setTimeout(() => {
          if (mapboxInstanceRef.current && googleCenter) {
            mapboxInstanceRef.current.jumpTo({
              center: [googleCenter.lng(), googleCenter.lat()],
              zoom: googleZoom || 15,
            });
            addMapboxMarkers();
          }
        }, 100);
      }, 0);
    } else {
      // 切到 Google：从 Mapbox 读取当前视图同步到 Google
      if (mapboxInstanceRef.current && map) {
        const mbCenter = mapboxInstanceRef.current.getCenter();
        const mbZoom = mapboxInstanceRef.current.getZoom();
        map.setCenter({ lat: mbCenter.lat, lng: mbCenter.lng });
        map.setZoom(mbZoom);
        setTimeout(() => {
          if (map) {
            google.maps.event.trigger(map, 'resize');
          }
        }, 100);
      }
      setMapProvider(provider);
      setMapboxNavExpanded(false);
    }
  }, [mapProvider, map, initMapbox, addMapboxMarkers]);

  // 图层切换：同时应用到 Google 与 Mapbox（Mapbox 未初始化时由 initMapbox 按当前图层建图）。
  // mapboxgl.Marker 是 DOM 覆盖物，setStyle 后仍保留，无需重建标记。
  useEffect(() => {
    map?.setMapTypeId(GOOGLE_MAP_TYPE_BY_LAYER[mapLayer]);
    const mbMap = mapboxInstanceRef.current;
    const targetStyle = MAPBOX_STYLE_BY_LAYER[mapLayer];
    if (mbMap && mapboxAppliedStyleRef.current !== targetStyle) {
      mbMap.setStyle(targetStyle);
      mapboxAppliedStyleRef.current = targetStyle;
    }
  }, [mapLayer, map]);

  // 当 review 数据变化时，如果 Mapbox 已初始化，更新 markers
  useEffect(() => {
    if (mapboxInstanceRef.current && review) {
      addMapboxMarkers();
    }
  }, [review?.id, addMapboxMarkers]);

  // 每次打开抽屉时重置状态，确保重新加载
  useEffect(() => {
    if (visible) {
      // 抽屉打开时，重置地图初始化标志，确保地图重新定位
      mapInitializedRef.current = false;
      lastCenterRef.current = null;
      lastSetCenterRef.current = null;
      setMapReady(false); // 重置地图准备状态
    } else {
      // 抽屉关闭时，清空所有状态
      setCorrectedLatitude('');
      setCorrectedLongitude('');
      setMapCenter(null);
      mapInitializedRef.current = false;
      lastCenterRef.current = null;
      lastSetCenterRef.current = null;
      setMapReady(false);
      setApprovedTypeValue(null);
      // 销毁 Mapbox 并重置为 Google 街道图
      destroyMapbox();
      setMapProvider('google');
      setMapLayer('streets');
    }
  }, [visible, destroyMapbox]);

  // 初始值优先用 approved_*，否则回填 reported_*，便于审核员快速确认
  useEffect(() => {
    if (!review) return;
    const initialPrimary = review.approvedPrimaryTypeCode ?? review.reportedPrimaryTypeCode ?? null;
    const initialSecondary = review.approvedSecondaryTypeCode ?? review.reportedSecondaryTypeCode ?? null;
    setApprovedTypeValue(initialPrimary && initialSecondary ? [initialPrimary, initialSecondary] : null);
  }, [review?.id, review?.approvedPrimaryTypeCode, review?.approvedSecondaryTypeCode, review?.reportedPrimaryTypeCode, review?.reportedSecondaryTypeCode]);

  // 每次 reviewId 改变时（包括相同 id 的重新打开），重置地图初始化标志
  useEffect(() => {
    if (reviewId && visible) {
      mapInitializedRef.current = false;
      lastCenterRef.current = null;
      lastSetCenterRef.current = null;
      setMapReady(false); // 重置地图准备状态
      // 切换到下一条时，loading 会卸载地图容器导致 Mapbox 实例与 DOM 脱钩，
      // 这里销毁旧实例并重置回 Google，避免重挂载后 Mapbox 白屏（用户可手动再点 Mapbox 重建）
      destroyMapbox();
      setMapProvider('google');
    }
  }, [reviewId, visible, destroyMapbox]);

  // 处理审核通过
  const handleApprove = async () => {
    if (!review || !reviewId) {
      message.warning('审核记录不存在');
      return;
    }
    
    if (!correctedLatitude || !correctedLongitude) {
      message.warning('请先移动地图设置修正后坐标');
      return;
    }

    setApproveLoading(true);
    try {
      const input: AddressLocationReviewReviewInput = {
        id: reviewId,
        reviewStatus: 'APPROVED',
        approvedLatitude: correctedLatitude,
        approvedLongitude: correctedLongitude,
        approvedPrimaryTypeCode: approvedTypeValue?.[0] ?? null,
        approvedSecondaryTypeCode: approvedTypeValue?.[1] ?? null,
      };

      await reviewAddressLocationReview({
        variables: { input },
      });
    } finally {
      setApproveLoading(false);
    }
  };

  // 处理拒绝
  const handleReject = async () => {
    if (!review || !reviewId) {
      message.warning('审核记录不存在');
      return;
    }

    setRejectLoading(true);
    try {
      const input: AddressLocationReviewReviewInput = {
        id: reviewId,
        reviewStatus: 'REJECTED',
        approvedPrimaryTypeCode: approvedTypeValue?.[0] ?? null,
        approvedSecondaryTypeCode: approvedTypeValue?.[1] ?? null,
      };

      await reviewAddressLocationReview({
        variables: { input },
      });
    } finally {
      setRejectLoading(false);
    }
  };

  // 坐标显示组件（带颜色标识）
  const CoordinateItem: React.FC<{
    label: string;
    value: string;
    color: string;
    editable?: boolean;
    onLatChange?: (lat: string) => void;
    onLngChange?: (lng: string) => void;
    disabled?: boolean;
    onClick?: () => void;
  }> = ({ label, value, color, editable = false, onLatChange, onLngChange, disabled = false, onClick }) => {
    const [lat, lng] = value ? value.split(', ') : ['', ''];
    
    // 使用统一的图标生成函数
    const pinIcon = generatePinIconDataUri(color, { width: 20, height: 24 });
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', width: '140px', flexShrink: 0 }}>
          <img
            src={pinIcon}
            alt=""
            style={{
              width: 20,
              height: 24,
              marginRight: 4,
              cursor: onClick ? 'pointer' : 'default',
            }}
            onClick={onClick}
            title={onClick ? '点击移动到该坐标' : undefined}
          />
          <Text type="secondary" className="text-sm" style={{ whiteSpace: 'nowrap' }}>{label}:</Text>
        </div>
        <div style={{ flex: 1 }}>
          {editable ? (
            <Space.Compact style={{ width: '100%' }}>
              <Input
                placeholder="纬度"
                value={lat}
                onChange={(e) => {
                  onLatChange?.(e.target.value);
                }}
                disabled={disabled}
                className="font-mono text-xs"
                size="small"
              />
              <Input
                placeholder="经度"
                value={lng}
                onChange={(e) => {
                  onLngChange?.(e.target.value);
                }}
                disabled={disabled}
                className="font-mono text-xs"
                size="small"
              />
            </Space.Compact>
          ) : (
            <Text className="text-sm font-mono" copyable={value ? { text: value } : false}>{value || '-'}</Text>
          )}
        </div>
      </div>
    );
  };

  if (loadError) {
    return (
      <Drawer
        title={
          <div className="flex items-center">
            <ArrowLeftOutlined
              className="mr-3 cursor-pointer hover:text-gray-600"
              onClick={handleClose}
            />
            <span className="text-lg font-medium">地址定位审核</span>
          </div>
        }
        placement="right"
        width="calc(100vw - 80px)"
        onClose={handleClose}
        open={visible}
        styles={{
          body: { padding: 0 },
          header: { borderBottom: '1px solid #f0f0f0' },
        }}
        closable={false}
      >
        <div className="p-6">
          <Text type="danger">地图加载失败，请刷新页面重试</Text>
        </div>
      </Drawer>
    );
  }

  return (
    <Drawer
        title={
          <div className="flex items-center">
            <ArrowLeftOutlined
              className="mr-3 cursor-pointer hover:text-gray-600"
              onClick={handleClose}
            />
            <span className="text-lg font-medium">地址定位审核</span>
          </div>
        }
        placement="right"
        width="calc(100vw - 80px)"
        onClose={handleClose}
      open={visible}
      styles={{
        body: { padding: 0 },
        header: { borderBottom: '1px solid #f0f0f0' },
      }}
      closable={false}
    >
      {loading ? (
        <div className="p-6">
          <Text>加载中...</Text>
        </div>
      ) : !review ? (
        <div className="p-6">
          <Text type="secondary">未找到审核记录</Text>
        </div>
      ) : (
        <div className="h-full bg-white flex">
          {/* 左侧地图区域 */}
          <div className="flex-1" style={{ height: 'calc(100vh - 64px)', position: 'relative' }}>
            {isLoaded && review ? (
              <>
                {/* Google Map */}
                <div style={{ width: '100%', height: '100%', display: mapProvider === 'google' ? 'block' : 'none' }}>
                  <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    center={center || { lat: parseFloat(review.reportedLatitude), lng: parseFloat(review.reportedLongitude) }}
                    zoom={15}
                    onLoad={onMapLoad}
                    onUnmount={onMapUnmount}
                    onCenterChanged={() => {
                      if (map && review?.status === 'PENDING' && !isUpdatingFromInputRef.current) {
                        const center = map.getCenter();
                        if (center) {
                          const lat = center.lat();
                          const lng = center.lng();
                          const latStr = lat.toFixed(7);
                          const lngStr = lng.toFixed(7);
                          correctedLatitudeRef.current = latStr;
                          correctedLongitudeRef.current = lngStr;
                          setCorrectedLatitude(latStr);
                          setCorrectedLongitude(lngStr);
                        }
                      }
                    }}
                    options={{
                      mapTypeControl: false,
                      streetViewControl: false,
                      fullscreenControl: false,
                      zoomControl: true,
                      draggable: true,
                      scrollwheel: true,
                      disableDoubleClickZoom: false,
                      gestureHandling: 'greedy',
                    }}
                  >
                    {staticMarkers}
                  </GoogleMap>
                </div>

                {/* Mapbox Map */}
                <div
                  ref={mapboxContainerRef}
                  style={{
                    width: '100%',
                    height: '100%',
                    display: mapProvider === 'mapbox' ? 'block' : 'none',
                  }}
                />

                {/* Mapbox 导航控件（对齐 Google Maps 样式和功能） */}
                {mapProvider === 'mapbox' && (() => {
                  const navBtn: React.CSSProperties = {
                    width: 40, height: 40, borderRadius: '50%', border: 'none',
                    background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                    cursor: 'pointer', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', padding: 0, color: '#666',
                  };
                  const atMaxZoom = mapboxInstanceRef.current ? mapboxZoom >= mapboxInstanceRef.current.getMaxZoom() : false;
                  const atMinZoom = mapboxInstanceRef.current ? mapboxZoom <= mapboxInstanceRef.current.getMinZoom() : false;
                  const disabledZoomIn: React.CSSProperties = atMaxZoom ? { opacity: 0.35, cursor: 'not-allowed' } : {};
                  const disabledZoomOut: React.CSSProperties = atMinZoom ? { opacity: 0.35, cursor: 'not-allowed' } : {};
                  return (
                    <div style={{ position: 'absolute', right: 10, bottom: 24, zIndex: 1000, display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 8 }}>
                      {/* 展开后的平移面板（仅上下左右） */}
                      {mapboxNavExpanded && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 40px)', gridTemplateRows: 'repeat(3, 40px)', gap: 4 }}>
                          <span />
                          <button style={navBtn} onClick={() => { mapboxInstanceRef.current?.panBy([0, -(mapboxContainerRef.current?.offsetHeight ?? 600) / 2]); setMapboxNavExpanded(false); }}>
                            <svg viewBox="0 0 24 24" width="26" height="26" fill="#555"><path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z"/></svg>
                          </button>
                          <button style={{ ...navBtn, fontSize: 26, fontWeight: 300, ...disabledZoomIn }} disabled={atMaxZoom} onClick={() => mapboxInstanceRef.current?.zoomIn()}>+</button>
                          <button style={navBtn} onClick={() => { mapboxInstanceRef.current?.panBy([-(mapboxContainerRef.current?.offsetWidth ?? 800) / 2, 0]); setMapboxNavExpanded(false); }}>
                            <svg viewBox="0 0 24 24" width="26" height="26" fill="#555"><path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6z"/></svg>
                          </button>
                          <span />
                          <button style={navBtn} onClick={() => { mapboxInstanceRef.current?.panBy([(mapboxContainerRef.current?.offsetWidth ?? 800) / 2, 0]); setMapboxNavExpanded(false); }}>
                            <svg viewBox="0 0 24 24" width="26" height="26" fill="#555"><path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z"/></svg>
                          </button>
                          <span />
                          <button style={navBtn} onClick={() => { mapboxInstanceRef.current?.panBy([0, (mapboxContainerRef.current?.offsetHeight ?? 600) / 2]); setMapboxNavExpanded(false); }}>
                            <svg viewBox="0 0 24 24" width="26" height="26" fill="#555"><path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z"/></svg>
                          </button>
                          <button style={{ ...navBtn, fontSize: 26, fontWeight: 300, ...disabledZoomOut }} disabled={atMinZoom} onClick={() => mapboxInstanceRef.current?.zoomOut()}>−</button>
                        </div>
                      )}
                      {/* 右侧列：切换按钮 + 始终显示的缩放按钮 */}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24, marginTop: 36 }}>
                      {/* 切换按钮（4方向箭头图标） */}
                      <button style={navBtn} onClick={() => setMapboxNavExpanded(v => !v)}>
                        <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="#555" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="9,5 12,2 15,5"/>
                          <polyline points="9,19 12,22 15,19"/>
                          <polyline points="5,9 2,12 5,15"/>
                          <polyline points="19,9 22,12 19,15"/>
                        </svg>
                      </button>
                      {/* 缩放按钮（始终显示） */}
                      <div style={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.3)', marginTop: 10 }}>
                        <button style={{ ...navBtn, borderRadius: 0, borderBottom: '1px solid #e0e0e0', fontSize: 30, fontWeight: 600, ...disabledZoomIn }} disabled={atMaxZoom} onClick={() => mapboxInstanceRef.current?.zoomIn()}>+</button>
                        <button style={{ ...navBtn, borderRadius: 0, fontSize: 30, fontWeight: 600, ...disabledZoomOut }} disabled={atMinZoom} onClick={() => mapboxInstanceRef.current?.zoomOut()}>−</button>
                      </div>
                    </div>
                    </div>
                  );
                })()}

                {/* 固定在地图中心的修正坐标图标（仅在待审核状态下显示） */}
                {review?.status === 'PENDING' && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -100%)',
                      pointerEvents: 'none',
                      zIndex: 1000,
                    }}
                  >
                    <img
                      src={generatePinIconDataUri(COORDINATE_COLORS.corrected, { width: 42, height: 52 })}
                      alt="修正后坐标"
                      style={{ width: '42px', height: '52px' }}
                    />
                  </div>
                )}

                {/* 左下角地图切换按钮 */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: 16,
                    left: 16,
                    zIndex: 1000,
                    display: 'flex',
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                  }}
                >
                  <button
                    onClick={() => handleSwitchProvider('google')}
                    style={{
                      padding: '6px 14px',
                      border: 'none',
                      cursor: mapProvider === 'google' ? 'default' : 'pointer',
                      fontSize: 13,
                      fontWeight: mapProvider === 'google' ? 600 : 400,
                      background: mapProvider === 'google' ? '#8b5cf6' : '#d1d5db',
                      color: '#fff',
                      borderRight: '1px solid #e5e7eb',
                    }}
                  >
                    Google
                  </button>
                  <button
                    onClick={() => handleSwitchProvider('mapbox')}
                    style={{
                      padding: '6px 14px',
                      border: 'none',
                      cursor: mapProvider === 'mapbox' ? 'default' : 'pointer',
                      fontSize: 13,
                      fontWeight: mapProvider === 'mapbox' ? 600 : 400,
                      background: mapProvider === 'mapbox' ? '#8b5cf6' : '#d1d5db',
                      color: '#fff',
                    }}
                  >
                    Mapbox
                  </button>
                </div>

                {/* 左下角图层切换按钮：街道 / 卫星，Google 与 Mapbox 共用 */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: 16,
                    left: 176,
                    zIndex: 1000,
                    display: 'flex',
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                  }}
                >
                  {(['streets', 'satellite'] as MapLayer[]).map((layer, index) => (
                    <button
                      key={layer}
                      onClick={() => setMapLayer(layer)}
                      style={{
                        padding: '6px 14px',
                        border: 'none',
                        cursor: mapLayer === layer ? 'default' : 'pointer',
                        fontSize: 13,
                        fontWeight: mapLayer === layer ? 600 : 400,
                        background: mapLayer === layer ? '#8b5cf6' : '#d1d5db',
                        color: '#fff',
                        borderRight: index === 0 ? '1px solid #e5e7eb' : 'none',
                      }}
                    >
                      {layer === 'streets' ? '街道' : '卫星'}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full">
                <Text type="secondary">加载地图中...</Text>
              </div>
            )}
          </div>

          {/* 右侧信息区域 */}
          <div className="w-[480px] border-l border-gray-200 flex flex-col" style={{ height: 'calc(100vh - 64px)' }}>
            <div className="flex-1 overflow-y-auto p-6">
              <Space direction="vertical" size="large" className="w-full">
                {/* 基本信息 */}
                <div>
                  <Text strong className="text-base mb-3 block">基本信息</Text>
                  <Space direction="vertical" size="middle" className="w-full">
                    <Row>
                      <Col span={6}>
                        <Text type="secondary" className="text-sm">原始地址:</Text>
                      </Col>
                      <Col span={18}>
                        {(() => {
                          // 后台修改来源用组件拼接格式：street building, city, state  zip
                          const originalAddressText = isBackendSource ? review.originalAddressFormatted : review.originalAddress;
                          return (
                            <Text className="text-sm" copyable={originalAddressText ? { text: originalAddressText } : false}>{originalAddressText || '-'}</Text>
                          );
                        })()}
                      </Col>
                    </Row>
                    <Row>
                      <Col span={6}>
                        <Text type="secondary" className="text-sm">标准地址:</Text>
                      </Col>
                      <Col span={18}>
                        <Text className="text-sm" copyable={review.standardAddress ? { text: review.standardAddress } : false}>{review.standardAddress || '-'}</Text>
                      </Col>
                    </Row>
                    <Row>
                      <Col span={6}>
                        <Text type="secondary" className="text-sm">运单号:</Text>
                      </Col>
                      <Col span={18}>
                        <Text className="text-sm" copyable={review.bizNo ? { text: review.bizNo } : false}>{review.bizNo || '-'}</Text>
                      </Col>
                    </Row>
                    <Row>
                      <Col span={6}>
                        <Text type="secondary" className="text-sm">上报来源:</Text>
                      </Col>
                      <Col span={18}>
                        <Text className="text-sm">{REPORT_SOURCE_LABELS[review.reportSource] || '-'}</Text>
                      </Col>
                    </Row>
                    {/* 后台修改来源无司机上报的地址类型，也不提供审核地址类型修正入口 */}
                    {!isBackendSource && (
                      <Row>
                        <Col span={6}>
                          <Text type="secondary" className="text-sm">司机上报类型:</Text>
                        </Col>
                        <Col span={18}>
                          <Text className="text-sm">
                            {formatTypeLabel(review.reportedPrimaryTypeCode, review.reportedSecondaryTypeCode)}
                          </Text>
                        </Col>
                      </Row>
                    )}
                    {!isBackendSource && (
                      <Row align="middle">
                        <Col span={6}>
                          <Text type="secondary" className="text-sm">审核地址类型:</Text>
                        </Col>
                        <Col span={18}>
                          <Cascader
                            options={typeCascaderOptions}
                            value={approvedTypeValue ?? undefined}
                            onChange={(value) => {
                              // 不允许清空到 null，否则会把已有的 approved_*_type_code 覆盖
                              if (value && (value as string[]).length === 2) {
                                setApprovedTypeValue(value as string[]);
                              }
                            }}
                            placeholder="请选择"
                            allowClear={false}
                            disabled={review.status !== 'PENDING'}
                            style={{ width: '100%' }}
                          />
                        </Col>
                      </Row>
                    )}
                  </Space>
                </div>

                {/* 坐标信息 */}
                <div>
                  <Text strong className="text-base mb-3 block">坐标信息</Text>
                  <Space direction="vertical" size="small" className="w-full">
                    <CoordinateItem
                      label="原始坐标"
                      value={`${review.latitude}, ${review.longitude}`}
                      color={COORDINATE_COLORS.original}
                      onClick={() => jumpToCoordinate(parseFloat(review.latitude), parseFloat(review.longitude))}
                    />
                    <CoordinateItem
                      label="待审坐标"
                      value={`${review.reportedLatitude}, ${review.reportedLongitude}`}
                      color={COORDINATE_COLORS.reported}
                      onClick={() => jumpToCoordinate(parseFloat(review.reportedLatitude), parseFloat(review.reportedLongitude))}
                    />
                    {!isBackendSource && review.driverLatitude && review.driverLongitude && (
                      <CoordinateItem
                        label="司机坐标"
                        value={`${review.driverLatitude}, ${review.driverLongitude}`}
                        color={COORDINATE_COLORS.driver}
                        onClick={() => jumpToCoordinate(parseFloat(review.driverLatitude!), parseFloat(review.driverLongitude!))}
                      />
                    )}
                    {!isBackendSource && review.deliveredLatitude && review.deliveredLongitude && (
                      <CoordinateItem
                        label="妥投坐标"
                        value={`${review.deliveredLatitude}, ${review.deliveredLongitude}`}
                        color={COORDINATE_COLORS.delivered}
                        onClick={() => jumpToCoordinate(parseFloat(review.deliveredLatitude!), parseFloat(review.deliveredLongitude!))}
                      />
                    )}
                    {review.status === 'APPROVED' && review.approvedLatitude && review.approvedLongitude && (
                      <CoordinateItem
                        label="审核通过坐标"
                        value={`${review.approvedLatitude}, ${review.approvedLongitude}`}
                        color={COORDINATE_COLORS.approved}
                        onClick={() => jumpToCoordinate(parseFloat(review.approvedLatitude!), parseFloat(review.approvedLongitude!))}
                      />
                    )}
                    {review.status === 'PENDING' && (
                      <CoordinateItem
                        label="修正后坐标"
                        value={correctedLatitude && correctedLongitude ? `${correctedLatitude}, ${correctedLongitude}` : ''}
                        color={COORDINATE_COLORS.corrected}
                        editable={false}
                      />
                    )}
                  </Space>
                </div>

                {/* POD 照片：后端经 order_address.order_id 取订单 POD 图片，最多 5 张；无照片时显示占位文案 */}
                <div>
                  <Text strong className="text-base mb-3 block">POD 照片</Text>
                  {(review.podImageUrls ?? []).length > 0 ? (
                    <Image.PreviewGroup>
                      <div className="grid grid-cols-5 gap-2">
                        {(review.podImageUrls ?? []).slice(0, 5).map((url, index) => (
                          <div key={`${index}-${url}`} className="aspect-[3/4] border border-gray-200 rounded overflow-hidden bg-gray-50">
                            <Image
                              src={url}
                              alt={`POD ${index + 1}`}
                              width="100%"
                              height="100%"
                              style={{ objectFit: 'cover' }}
                            />
                          </div>
                        ))}
                      </div>
                    </Image.PreviewGroup>
                  ) : (
                    <Text type="secondary" className="text-sm">无</Text>
                  )}
                </div>

              </Space>
            </div>


            <HasResource resourceCodes={[ResourceCode.locationCorrection.REVIEW_SUBMIT]}>
            {/* 操作按钮 - 固定在底部 */}
            {review.status === 'PENDING' && (
              <div className="border-t border-gray-200 p-4 flex justify-end gap-3">
                <Button
                  onClick={handleReject}
                  style={{ backgroundColor: '#cf1322', borderColor: '#cf1322', color: '#fff' }}
                  loading={rejectLoading}
                  disabled={rejectLoading || approveLoading}
                >
                  拒绝
                </Button>
                <Button
                  type="primary"
                  onClick={handleApprove}
                  style={{ backgroundColor: '#389e0d', borderColor: '#389e0d' }}
                  loading={approveLoading}
                  disabled={approveLoading || rejectLoading}
                >
                  审核通过
                </Button>
              </div>
            )}
            </HasResource>
          </div>
        </div>
      )}
    </Drawer>
  );
};

export default AddressLocationReviewDrawer;
